import React, { useEffect, useState } from "react";
import { Add, Remove } from "@mui/icons-material";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  List,
  ListItem,
  ListSubheader,
} from "@mui/material";
import { advancePhase } from "../ProjectActions";
import { useDispatch } from "react-redux";

export default function SegmentsSelect({ project, isFirstPhase, design, open, onClose }) {
  const dispatch = useDispatch();
  const [segments, setSegments] = useState([
    { name: "Constructiefase", length: 0 },
    { name: "Gebruiksfase", length: 0 },
  ]);
  const [copyFromPrevious, setCopyFromPrevious] = useState(!isFirstPhase);

  const [defaultName, setDefaultName] = useState("Fase");
  const [phase, setPhase] = useState(0);
  const [customPhaseName, setCustomPhaseName] = useState("");
  const [useDefaultName, setUseDefaultName] = useState(false);
  const totalLength = segments.reduce((a, b) => a + Number(b.length), 0);
  const previousTotalLength = isFirstPhase ? 0 : project.phases[0].segments.reduce((a, b) => a + Number(b.length), 0);
  // To help the user, generate a string with the names chosen layers
  const layersString = project.potential_layers
    .filter((layer) => project.layers.includes(layer.id))
    .map((layer) => layer.name)
    .join(", ");

  // Wrapping what should be the default for the copyFromPrevious and segments useStates in a useEffect,
  // to force re-rendering when switching between projects
  useEffect(() => {
    if (copyFromPrevious && !isFirstPhase) {
      setSegments(project.phases[0].segments);
    }
  }, [copyFromPrevious]);
  useEffect(() => {
    setCopyFromPrevious(!isFirstPhase);
  }, [isFirstPhase]);

  const handleAddSegment = () => {
    setSegments([...segments, { name: useDefaultName ? `${defaultName} ${segments.length + 1}` : "", length: 0 }]);
  };
  const handleRemoveSegment = (index) => {
    if (index !== null) {
      setSegments(segments.filter((segment, i) => i !== index));
    } else if (segments.length > 0) {
      setSegments(segments.slice(0, -1));
    }
  };
  const handleSetSegmentNumber = (number) => {
    if (Number(number) === 0) {
      null;
    } else if (number < segments.length) {
      setSegments(segments.slice(0, number));
    } else {
      setSegments([
        ...segments,
        ...Array(number - segments.length)
          .fill({ name: "", length: 0 })
          .map((segment, index) => ({ ...segment, name: `${defaultName} ${index + segments.length + 1}` })),
      ]);
    }
  };
  const handleChangeDefaultName = (name) => {
    setDefaultName(name);
    setSegments(segments.map((segment, index) => ({ ...segment, name: `${name} ${index + 1}` })));
  };
  const handleChangeUseDefaultName = (value) => {
    if (value) {
      setUseDefaultName(true);
      setSegments(segments.map((segment, index) => ({ ...segment, name: `${defaultName} ${index + 1}` })));
    } else {
      setUseDefaultName(false);
    }
  };
  const handleSaveAndContinue = () => {
    const phase_name = phase == 0 ? "Verkenningsfase" : phase == 1 ? "Planfase" : customPhaseName;

    advancePhase(project.id, segments, design, phase, phase_name, dispatch);
    onClose();
  };


  return (
    <Dialog open={open} onClose={onClose} scroll="paper" sx={{ verticalAlign: "top", height: "100%" }}>
      <DialogTitle>Gebruiksfases</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {design ? (
            <Tooltip title={design.description}>
              <Typography>In de volgende fase wordt het volgende ontwerp meegenomen: {design.name}</Typography>
            </Tooltip>
          ) : (
            ""
          )}
          <Typography>
            U kunt hier kiezen welke naam u geeft aan de huidige fase in het ontwerpproces. Selecteer een naam voor de
            fase, of voer zelf een optie in.
          </Typography>
          <FormControl>
            <RadioGroup
              row
              aria-labelledby="demo-radio-buttons-group-label"
              defaultValue="0"
              name="radio-buttons-group"
              onChange={(event) => {
                setPhase(event.target.value);
              }}
            >
              <FormControlLabel value="0" control={<Radio />} label="Verkenningsfase" />
              <FormControlLabel value="1" control={<Radio />} label="Planfase" />
              <FormControlLabel value="2" control={<Radio />} label="Anders"></FormControlLabel>
              {phase == 2 ? (
                <TextField
                  value={customPhaseName}
                  onChange={(event) => {
                    setCustomPhaseName(event.target.value);
                  }}
                  placeholder="Naam van de ontwerpfase"
                />
              ) : (
                ""
              )}
            </RadioGroup>
          </FormControl>
          <Typography>
            U kunt hier kiezen welke gebruiksfases worden gebruikt in het ontwerpproces. Samen met de{" "}
            <Tooltip component="span" title={layersString || "nog geen onderdelen geselecteerd"}>
              <u>gekozen onderdelen uit de Opstartfase</u>
            </Tooltip>
            , bepalen deze gebruiksfases de manier waarop bepaalde KPI's worden ingevuld.
          </Typography>
          {!isFirstPhase && (
            <FormControlLabel
              control={<Switch checked={copyFromPrevious} onChange={(e) => setCopyFromPrevious(e.target.checked)} />}
              label={`Gebruik dezelfde gebruiksfases als de ${project.phases.slice(-1)[0].phase_name}`}
            />
          )}
          {!copyFromPrevious && (
            <>
              <Stack direction="row" spacing={1}>
                <IconButton onClick={() => handleRemoveSegment(null)} disabled={copyFromPrevious}>
                  <Remove />
                </IconButton>
                <TextField
                  label="Aantal gebruiksfases"
                  type="number"
                  min={1}
                  value={segments.length}
                  onChange={(e) => handleSetSegmentNumber(e.target.value)}
                  disabled={copyFromPrevious}
                />
                <IconButton onClick={handleAddSegment} disabled={copyFromPrevious}>
                  <Add />
                </IconButton>
              </Stack>
            </>
          )}
          <List>
            {segments.map((segment, index) => (
              <ListItem key={`segmentItem-${index}`}>
                <Typography>{index + 1}.</Typography>
                <TextField
                  key={`segmentTextField-${index}`}
                  disabled={useDefaultName || copyFromPrevious}
                  label="Naam van fase"
                  value={segment.name}
                  onChange={(e) =>
                    setSegments(segments.map((s, i) => (i === index ? { ...s, name: e.target.value } : s)))
                  }
                />
              </ListItem>
            ))}
          </List>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuleren</Button>
        <Button
          onClick={handleSaveAndContinue}
          disabled={segments.length === 0 || segments.some((segment) => segment.name === "")}
        >
          Opslaan en volgende fase
        </Button>
      </DialogActions>
    </Dialog>
  );
}
